/* eslint-disable sonarjs/no-hardcoded-passwords -- Field-key mapping dictionaries contain "password" as a field name, not a credential value */
import * as yup from "yup";
import { PropertyManagementSystem } from "shared/types/partner";
import { SyncOption } from "../constants";
import { type CredentialFormProps } from "./types";
import { AppfolioCredentialForm } from "./AppfolioCredentialForm";
import { BuildiumCredentialForm } from "./BuildiumCredentialForm";
import { EntrataCredentialForm } from "./EntrataCredentialForm";
import { ManageAmericaCredentialForm } from "./ManageAmericaCredentialForm";
import { PropertywareCredentialForm } from "./PropertywareCredentialForm";
import { QuextCredentialForm } from "./QuextCredentialForm";
import { RentManagerCredentialForm } from "./RentManagerCredentialForm";
import { RentersWarehouseCredentialForm } from "./RentersWarehouseCredentialForm";
import { RentvineCredentialForm } from "./RentvineCredentialForm";
import { ResmanCredentialForm } from "./ResmanCredentialForm";
import { RevelaCredentialForm } from "./RevelaCredentialForm";
import { YardiCredentialForm } from "./YardiCredentialForm";

const req = (label: string) => yup.string().required(`${label} is required`);
const opt = () => yup.string().optional();

interface CredentialRegistryEntry {
  defaults: Record<string, string | boolean>;
  schema: yup.AnyObjectSchema;
  Form: React.FC<CredentialFormProps>;
  fromSource: (
    src: Record<string, unknown>
  ) => Record<string, string | boolean>;
}

const applyDefaults = (
  defaults: Record<string, string | boolean>,
  raw: Record<string, unknown>
): Record<string, string | boolean> => ({
  ...defaults,
  ...Object.fromEntries(
    Object.entries(raw)
      .filter(([, v]) => v != null && v !== "")
      .map(([k, v]) =>
        typeof defaults[k] === "boolean"
          ? [k, v === true || v === "true"]
          : [k, String(v)]
      )
  )
});

const buildFromSource =
  (defaults: Record<string, string | boolean>) =>
  (src: Record<string, unknown>): Record<string, string | boolean> =>
    applyDefaults(
      defaults,
      Object.fromEntries(Object.keys(defaults).map((k) => [k, src[k]]))
    );

const makeEntry = (input: {
  defaults: Record<string, string | boolean>;
  schema: yup.AnyObjectSchema;
  Form: React.FC<CredentialFormProps>;
}): CredentialRegistryEntry => ({
  ...input,
  fromSource: buildFromSource(input.defaults)
});

type ActivePMS = Exclude<
  PropertyManagementSystem,
  PropertyManagementSystem.Other
>;

export const CREDENTIAL_REGISTRY: Record<ActivePMS, CredentialRegistryEntry> = {
  [PropertyManagementSystem.Appfolio]: makeEntry({
    defaults: {
      appfolio_username: "",
      appfolio_password: "",
      appfolio_url: "",
      appfolio_sync: SyncOption.EVERY_SYNC,
      appfolio_use_reports: false,
      appfolio_use_property_groups: false,
      appfolio_selected_property_groups: "",
      appfolio_enable_rently: false,
      appfolio_use_supergood_api: false,
      appfolio_supergood_username: "",
      appfolio_supergood_password: "",
      appfolio_supergood_mfa_phone: ""
    },
    schema: yup.object({
      appfolio_username: req("Username"),
      appfolio_password: req("Password"),
      appfolio_url: req("URL"),
      appfolio_sync: yup
        .string()
        .oneOf(Object.values(SyncOption))
        .required("Sync option is required"),
      appfolio_use_reports: yup.boolean().optional(),
      appfolio_use_property_groups: yup.boolean().optional(),
      appfolio_selected_property_groups: opt(),
      appfolio_enable_rently: yup.boolean().optional(),
      appfolio_use_supergood_api: yup.boolean().optional(),
      appfolio_supergood_username: opt(),
      appfolio_supergood_password: opt(),
      appfolio_supergood_mfa_phone: opt()
    }),
    Form: AppfolioCredentialForm
  }),

  [PropertyManagementSystem.Buildium]: makeEntry({
    defaults: {
      buildium_client_id: "",
      buildium_client_secret: "",
      buildium_email: "",
      buildium_password: "",
      buildium_subdomain: "",
      buildium_sync: SyncOption.NEVER
    },
    schema: yup.object({
      buildium_client_id: req("Client ID"),
      buildium_client_secret: req("Client secret"),
      buildium_email: opt(),
      buildium_password: opt(),
      buildium_subdomain: opt(),
      buildium_sync: yup
        .string()
        .oneOf(Object.values(SyncOption))
        .required("Sync option is required")
    }),
    Form: BuildiumCredentialForm
  }),

  [PropertyManagementSystem.Entrata]: makeEntry({
    defaults: {
      entrata_domain: "",
      entrata_username: "",
      entrata_password: "",
      entrata_mfa_email: ""
    },
    schema: yup.object({
      entrata_domain: req("Domain"),
      entrata_username: opt(),
      entrata_password: opt(),
      entrata_mfa_email: opt()
    }),
    Form: EntrataCredentialForm
  }),

  [PropertyManagementSystem.ManageAmerica]: makeEntry({
    defaults: { manage_america_company_id: "" },
    schema: yup.object({ manage_america_company_id: req("Company ID") }),
    Form: ManageAmericaCredentialForm
  }),

  [PropertyManagementSystem.Propertyware]: makeEntry({
    defaults: {
      propertyware_client_id: "",
      propertyware_client_secret: "",
      propertyware_system_id: "",
      propertyware_market_id: "",
      propertyware_sync: SyncOption.EVERY_SYNC,
      propertyware_email: "",
      propertyware_password: "",
      propertyware_mfa_phone: ""
    },
    schema: yup.object({
      propertyware_client_id: req("Client ID"),
      propertyware_client_secret: req("Client Secret"),
      propertyware_system_id: req("System ID"),
      propertyware_market_id: opt(),
      propertyware_sync: opt(),
      propertyware_email: opt(),
      propertyware_password: opt(),
      propertyware_mfa_phone: opt()
    }),
    Form: PropertywareCredentialForm
  }),

  [PropertyManagementSystem.Quext]: makeEntry({
    defaults: { quext_customer_id: "" },
    schema: yup.object({ quext_customer_id: req("Customer ID") }),
    Form: QuextCredentialForm
  }),

  [PropertyManagementSystem.RentManager]: makeEntry({
    defaults: {
      rent_manager_company_code: "",
      rent_manager_sync: SyncOption.EVERY_SYNC,
      rent_manager_sync_assets: false
    },
    schema: yup.object({
      rent_manager_company_code: req("Rent Manager company code"),
      rent_manager_sync: yup
        .string()
        .oneOf(Object.values(SyncOption))
        .required("Sync option is required")
    }),
    Form: RentManagerCredentialForm
  }),

  [PropertyManagementSystem.Revela]: makeEntry({
    defaults: { revela_subdomain: "" },
    schema: yup.object({ revela_subdomain: req("Subdomain") }),
    Form: RevelaCredentialForm
  }),

  [PropertyManagementSystem.RentersWarehouse]: makeEntry({
    defaults: {
      renters_warehouse_api_key: "",
      renters_warehouse_url: "",
      renters_warehouse_salesforce_domain: "",
      renters_warehouse_salesforce_client_id: "",
      renters_warehouse_salesforce_client_secret: "",
      renters_warehouse_salesforce_username: "",
      renters_warehouse_salesforce_password: ""
    },
    schema: yup.object({
      renters_warehouse_api_key: req("API Key"),
      renters_warehouse_url: req("URL"),
      renters_warehouse_salesforce_domain: req("Salesforce Domain"),
      renters_warehouse_salesforce_client_id: req("Salesforce Client ID"),
      renters_warehouse_salesforce_client_secret: req(
        "Salesforce Client Secret"
      ),
      renters_warehouse_salesforce_username: req("Salesforce Username"),
      renters_warehouse_salesforce_password: req("Salesforce Password")
    }),
    Form: RentersWarehouseCredentialForm
  }),

  [PropertyManagementSystem.Resman]: makeEntry({
    defaults: {
      resman_partner_id: "",
      resman_account_id: "",
      resman_api_key: ""
    },
    schema: yup.object({
      resman_partner_id: req("Integration Partner ID"),
      resman_account_id: req("Account ID"),
      resman_api_key: req("API Key")
    }),
    Form: ResmanCredentialForm
  }),

  [PropertyManagementSystem.Rentvine]: makeEntry({
    defaults: {
      rentvine_account: "",
      rentvine_api_key: "",
      rentvine_api_secret: "",
      rentvine_sync: SyncOption.NEVER
    },
    schema: yup.object({
      rentvine_account: req("Account"),
      rentvine_api_key: req("API Key"),
      rentvine_api_secret: req("API Secret"),
      rentvine_sync: yup
        .string()
        .oneOf(Object.values(SyncOption))
        .required("Sync option is required")
    }),
    Form: RentvineCredentialForm
  }),

  [PropertyManagementSystem.Yardi]: makeEntry({
    defaults: {
      yardi_web_service_url: "",
      yardi_username: "",
      yardi_password: "",
      yardi_server_name: "",
      yardi_database: "",
      yardi_enable_application_charge: false,
      yardi_application_charge_code: "",
      yardi_enable_screening_services_charge: false,
      yardi_screening_services_charge_code: "",
      yardi_general_ledger_account: "",
      yardi_enable_template_fee_charges: false,
      yardi_administration_fee_charge_code: "",
      yardi_holding_fee_charge_code: "",
      yardi_use_lease_term: false
    },
    schema: yup.object({
      yardi_web_service_url: req("URL"),
      yardi_username: req("User name"),
      yardi_password: req("Password"),
      yardi_server_name: req("Server name"),
      yardi_database: req("Database"),
      yardi_enable_application_charge: yup.boolean().optional(),
      yardi_application_charge_code: opt(),
      yardi_enable_screening_services_charge: yup.boolean().optional(),
      yardi_screening_services_charge_code: opt(),
      yardi_general_ledger_account: opt(),
      yardi_enable_template_fee_charges: yup.boolean().optional(),
      yardi_administration_fee_charge_code: opt(),
      yardi_holding_fee_charge_code: opt(),
      yardi_use_lease_term: yup.boolean().optional()
    }),
    Form: YardiCredentialForm
  })
};

// Vendor-agnostic General-tab keys stored as-is in the partner settings JSON
export const COMMON_SETTINGS_KEYS: ReadonlySet<string> = new Set([
  "pms_prospect_creation_trigger",
  "pms_prospect_creation_on_approve",
  "pms_prospect_creation_on_conditional_approve",
  "pms_prospect_creation_on_decline",
  "rent_manager_create_twa",
  "rent_manager_push_inbox_communications",
  "rent_manager_push_lead_stage_changes",
  "rent_manager_sync_assets",
  "pms_import_inventory",
  "appfolio_create_guest_cards",
  "manage_america_pet_data_source"
]);

const toCamel = (s: string) =>
  s.replace(/_([a-z])/g, (_, c: string) => c.toUpperCase());

// Maps from "true"/"false" strings to booleans for legacy boolean settings.
const coerceBoolString = (value: string): string | boolean => {
  if (value === "true") return true;
  if (value === "false") return false;
  return value;
};

// Converts prefixed snake_case form values to the camelCase shape expected
// by the legacy saveSettings endpoint. Common general-tab keys are excluded
// since they are not PMS credential fields.
export function toSettingsPayload(
  values: Record<string, string | boolean>
): Record<string, unknown> {
  return Object.fromEntries(
    Object.entries(values)
      .filter(([k]) => !COMMON_SETTINGS_KEYS.has(k))
      .map(([k, v]) => [
        toCamel(k),
        typeof v === "string" ? coerceBoolString(v) : v
      ])
  );
}
